import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { actionPriceService } from "@/api/services"
import type {
  ActionPriceResponseDto,
  ActionPriceListParams,
  ActionPriceCategory,
  ActionType,
} from "@/api/types/actionPrice.types"
import Sidebar from "@/components/Sidebar"
import FileUpload from "@/components/FileUpload"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, ChevronLeft, ChevronRight, Plus, X } from "lucide-react"

export default function ActionPricesListPage() {
  const navigate = useNavigate()
  const [actionPrices, setActionPrices] = useState<ActionPriceResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Filters and pagination
  const [params, setParams] = useState<ActionPriceListParams>({
    page: 1,
    limit: 50,
  })
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState<ActionPriceCategory | undefined>(
    undefined
  )
  const [actionTypeFilter, setActionTypeFilter] = useState<ActionType | undefined>(undefined)
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined)

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Form states
  const [formData, setFormData] = useState<{
    category: ActionPriceCategory
    actionType: ActionType
    costCredits: string
    durationDays: string
    isActive: boolean
    iconUploadedFileId: string
  }>({
    category: "Motorcycle",
    actionType: "Publish",
    costCredits: "",
    durationDays: "",
    isActive: true,
    iconUploadedFileId: "",
  })
  const [saving, setSaving] = useState(false)

  // Auto-apply filters when they change
  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      page: 1,
      category: categoryFilter,
      actionType: actionTypeFilter,
      isActive: isActiveFilter,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, actionTypeFilter, isActiveFilter])

  useEffect(() => {
    loadActionPrices()
  }, [params])

  const loadActionPrices = async () => {
    setLoading(true)
    setError("")
    try {
      const response = await actionPriceService.getActionPricesAdmin(params)
      
      // Debug: Log the response to see its structure
      console.log("API Response:", response)
      console.log("Is Array?", Array.isArray(response))
      console.log("Response type:", typeof response)
      
      // Handle both array response and object with data property
      let prices: ActionPriceResponseDto[] = []
      let totalCount = 0
      let currentPageNum = 1
      let totalPagesCount = 1
      
      if (Array.isArray(response)) {
        // API returns array directly
        console.log("Handling as array, length:", response.length)
        prices = response
        totalCount = response.length
        currentPageNum = params.page || 1
        totalPagesCount = 1
      } else if (response && typeof response === 'object' && Array.isArray(response.data)) {
        // API returns object with data property
        console.log("Handling as object with data property, length:", response.data.length)
        prices = response.data
        totalCount = response.total || response.data.length
        currentPageNum = response.page || 1
        totalPagesCount = response.totalPages || Math.ceil(totalCount / (params.limit || 50))
      } else {
        console.warn("Unexpected response format:", response)
      }
      
      console.log("Setting prices:", prices.length, "items")
      setActionPrices(prices)
      setTotal(totalCount)
      setCurrentPage(currentPageNum)
      setTotalPages(totalPagesCount)
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to load action prices"
      setError(errorMessage)
      setActionPrices([])
      setTotal(0)
      setCurrentPage(1)
      setTotalPages(1)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setFormData({
      category: "Motorcycle",
      actionType: "Publish",
      costCredits: "",
      durationDays: "",
      isActive: true,
      iconUploadedFileId: "",
    })
    setCreateDialogOpen(true)
  }

  const handleActionPriceClick = (actionPrice: ActionPriceResponseDto) => {
    navigate(`/action-prices/${actionPrice.id}`)
  }

  const handleSubmitCreate = async () => {
    if (
      !formData.costCredits ||
      isNaN(Number(formData.costCredits)) ||
      Number(formData.costCredits) < 0
    ) {
      setError("Valid cost credits is required")
      return
    }

    if (
      !formData.durationDays ||
      isNaN(Number(formData.durationDays)) ||
      Number(formData.durationDays) < 0
    ) {
      setError("Valid duration days is required")
      return
    }

    setSaving(true)
    setError("")
    setSuccess("")

    try {
      await actionPriceService.createActionPrice({
        category: formData.category,
        actionType: formData.actionType,
        costCredits: Number(formData.costCredits),
        durationDays: Number(formData.durationDays),
        isActive: formData.isActive,
        iconUploadedFileId: formData.iconUploadedFileId.trim() || undefined,
      })
      setSuccess("Action price created successfully")
      setCreateDialogOpen(false)
      setFormData({
        category: "Motorcycle",
        actionType: "Publish",
        costCredits: "",
        durationDays: "",
        isActive: true,
        iconUploadedFileId: "",
      })
      loadActionPrices()
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to create action price"
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleClearFilters = () => {
    setCategoryFilter(undefined)
    setActionTypeFilter(undefined)
    setIsActiveFilter(undefined)
  }

  const handlePageChange = (newPage: number) => {
    setParams({ ...params, page: newPage })
  }

  const hasActiveFilters =
    categoryFilter !== undefined ||
    actionTypeFilter !== undefined ||
    isActiveFilter !== undefined

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Action Prices</h1>
              <p className="text-muted-foreground">Manage action prices in the system</p>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Action Price
            </Button>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={categoryFilter || ""}
                  onChange={(e) =>
                    setCategoryFilter(
                      e.target.value === "" ? undefined : (e.target.value as ActionPriceCategory)
                    )
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All Categories</option>
                  <option value="Motorcycle">Motorcycle</option>
                  <option value="Gear">Gear</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="actionType">Action Type</Label>
                <select
                  id="actionType"
                  value={actionTypeFilter || ""}
                  onChange={(e) =>
                    setActionTypeFilter(
                      e.target.value === "" ? undefined : (e.target.value as ActionType)
                    )
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All Action Types</option>
                  <option value="Publish">Publish</option>
                  <option value="Renew">Renew</option>
                  <option value="Highlight">Highlight</option>
                  <option value="Top">Top</option>
                  <option value="Urgent">Urgent</option>
                  <option value="HomepageHighlight">HomepageHighlight</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="isActive">Status</Label>
                <select
                  id="isActive"
                  value={isActiveFilter === undefined ? "" : isActiveFilter.toString()}
                  onChange={(e) => {
                    const value = e.target.value
                    setIsActiveFilter(value === "" ? undefined : value === "true")
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={handleClearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Action Prices Table */}
          <Card>
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !actionPrices || actionPrices.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-2">No action prices found</p>
                {hasActiveFilters && (
                  <p className="text-xs">
                    Try clearing your filters to see all action prices
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto px-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Action Type</TableHead>
                        <TableHead>Cost Credits</TableHead>
                        <TableHead>Duration (Days)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created At</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {actionPrices.map((actionPrice) => (
                        <TableRow
                          key={actionPrice.id}
                          className="cursor-pointer hover:bg-accent/50 transition-colors"
                          onClick={() => handleActionPriceClick(actionPrice)}
                        >
                          <TableCell className="font-medium">
                            {actionPrice.category}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{actionPrice.actionType}</Badge>
                          </TableCell>
                          <TableCell>{actionPrice.costCredits}</TableCell>
                          <TableCell>{actionPrice.durationDays}</TableCell>
                          <TableCell>
                            <Badge
                              variant={actionPrice.isActive ? "default" : "secondary"}
                            >
                              {actionPrice.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(actionPrice.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-muted-foreground text-sm">
                              Click to view
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * (params.limit || 50)) + 1} to{" "}
                      {Math.min(currentPage * (params.limit || 50), total)} of {total}{" "}
                      action prices
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Action Price</DialogTitle>
            <DialogDescription>
              Add a new action price to the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-category">
                  Category <span className="text-destructive">*</span>
                </Label>
                <select
                  id="create-category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: e.target.value as ActionPriceCategory,
                    })
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="Motorcycle">Motorcycle</option>
                  <option value="Gear">Gear</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-actionType">
                  Action Type <span className="text-destructive">*</span>
                </Label>
                <select
                  id="create-actionType"
                  value={formData.actionType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      actionType: e.target.value as ActionType,
                    })
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="Publish">Publish</option>
                  <option value="Renew">Renew</option>
                  <option value="Highlight">Highlight</option>
                  <option value="Top">Top</option>
                  <option value="Urgent">Urgent</option>
                  <option value="HomepageHighlight">HomepageHighlight</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-costCredits">
                  Cost Credits <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-costCredits"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="0"
                  value={formData.costCredits}
                  onChange={(e) =>
                    setFormData({ ...formData, costCredits: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-durationDays">
                  Duration (Days) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-durationDays"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="0"
                  value={formData.durationDays}
                  onChange={(e) =>
                    setFormData({ ...formData, durationDays: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <FileUpload
                label="Icon (Optional)"
                description="Upload an icon for this action price"
                value={formData.iconUploadedFileId || undefined}
                onChange={(fileId) =>
                  setFormData({ ...formData, iconUploadedFileId: fileId || "" })
                }
                previewSize="sm"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="create-isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="create-isActive" className="cursor-pointer">
                  Active
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitCreate} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
